import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaSpinner, FaPaperPlane, FaExclamationCircle, FaEnvelope, FaPhone, FaGlobe } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { submitContactForm, checkExistingContact } from '../../../services/contactService';

const ContactCTA = ({ 
  title = "Ready to innovate with us?", 
  subtitle = "Get in touch today to discuss how we can help transform your business.",
  buttonText = "Contact Us",
  theme = 'dark'
}) => {
  const isDark = theme === 'dark';
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [existingContact, setExistingContact] = useState(null);
  const [errors, setErrors] = useState({});
  const [showForm, setShowForm] = useState(false);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { 
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const checkForExistingContact = async (email, message) => {
    try {
      setChecking(true);
      const response = await checkExistingContact(email, message);
      setExistingContact(response.existingContact);
      
      if (response.existingContact) {
        const { status, createdAt } = response.existingContact;
        const timeAgo = new Date(createdAt).toLocaleDateString();
        
        if (status === 'new' || status === 'in-progress') {
          toast.info(`You have a ${status} inquiry from ${timeAgo}. Our team will respond shortly.`);
          return true;
        }
      }
      return false;
    } catch (error) {
      console.error('Error checking existing contact:', error);
      return false;
    } finally {
      setChecking(false);
    }
  };

  const handleChange = async (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    if (name === 'email' || name === 'message') {
      setExistingContact(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    const hasExisting = await checkForExistingContact(formData.email, formData.message);
    if (hasExisting) {
      toast.info('Please wait for our response to your existing inquiry before submitting a similar message.', {
        autoClose: 6000
      });
      return;
    }
    
    setLoading(true);
    try {
      const response = await submitContactForm(formData);
      toast.success(response.message);
      const referenceNumber = response.contact.id.slice(-6).toUpperCase();
      toast.info(`Your reference number is #${referenceNumber}`, {
        autoClose: 8000
      });
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: ''
      });
      setExistingContact(null);
      setShowForm(false);
    } catch (error) {
      if (error.response?.status === 409) {
        toast.warning(error.response.data.message);
      } else {
        toast.error(error.response?.data?.message || 'Failed to send message');
      }
    } finally {
      setLoading(false);
    }
  };

  const inputClasses = (error) => `
    min-w-0 flex-auto rounded-md border-0 px-3.5 py-2 shadow-sm ring-1 ring-inset focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:text-sm sm:leading-6 ${
      isDark 
        ? 'bg-white/5 text-white ring-white/10'
        : 'bg-white text-gray-900 ring-gray-300'
    } ${error ? 'ring-red-500' : ''}
  `;

  const getStatusMessage = () => {
    if (!existingContact) return null;
    const { status, createdAt, _id } = existingContact;
    const timeAgo = new Date(createdAt).toLocaleDateString();
    const referenceNumber = _id.slice(-6).toUpperCase();
    
    if (status === 'new' || status === 'in-progress') {
      return (
        <div className="mb-6 space-y-4">
          <div className={`p-4 ${isDark ? 'bg-yellow-900/50' : 'bg-yellow-50'} border-l-4 border-yellow-500 rounded-r-lg`}>
            <div className="flex items-start space-x-3">
              <FaExclamationCircle className="h-5 w-5 text-yellow-500 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
              <div className="space-y-2">
                <p className={`font-medium ${isDark ? 'text-yellow-200' : 'text-yellow-700'}`}>
                  Existing Inquiry In Progress
                </p>
                <p className={`text-sm ${isDark ? 'text-yellow-300' : 'text-yellow-600'}`}>
                  You have an active inquiry from {timeAgo} (Ref: #{referenceNumber}) that is currently {status}.
                  Our team will respond to your previous message soon.
                </p>
                <div className="text-sm">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    isDark ? 'bg-yellow-900 text-yellow-200' : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    Status: {status.charAt(0).toUpperCase() + status.slice(1)}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className={`text-sm italic ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            To avoid duplicate inquiries, please wait for our response or submit a different message.
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <section 
      className={`relative isolate overflow-hidden py-16 sm:py-24 lg:py-32 ${
        isDark 
          ? 'bg-gradient-to-r from-gray-900 to-black'
          : 'bg-gradient-to-r from-gray-100 to-white'
      }`}
    >
      <motion.div 
        className="mx-auto max-w-7xl px-6 lg:px-8"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={containerVariants}
      >
        <motion.div 
          className="mx-auto grid max-w-2xl grid-cols-1 gap-x-8 gap-y-16 lg:max-w-none lg:grid-cols-2"
          variants={containerVariants}
        >
          <motion.div 
            className="max-w-xl lg:max-w-lg"
            variants={itemVariants}
          >
            <h2 className={`text-4xl font-semibold tracking-tight mb-6 ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>
              {title}
            </h2>
            <p className={`mt-4 text-lg ${
              isDark ? 'text-gray-300' : 'text-gray-600'
            }`}>
              {subtitle}
            </p>

            {!showForm ? (
              <motion.button
                onClick={() => setShowForm(true)}
                className={`mt-6 rounded-md px-6 py-3 text-base font-semibold text-white shadow-sm transition-all duration-300 transform hover:scale-105 active:scale-95 ${
                  isDark 
                    ? 'bg-blue-600 hover:bg-blue-700'
                    : 'bg-blue-500 hover:bg-blue-600'
                }`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {buttonText}
              </motion.button>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-6"
              >
                {getStatusMessage()}
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Your full name"
                      className={inputClasses(errors.name)}
                    />
                    {errors.name && (
                      <p className={`mt-2 text-sm ${isDark ? 'text-red-400' : 'text-red-600'}`}>{errors.name}</p>
                    )}
                  </div>

                  <div>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your.email@example.com"
                      className={inputClasses(errors.email)}
                    />
                    {errors.email && (
                      <p className={`mt-2 text-sm ${isDark ? 'text-red-400' : 'text-red-600'}`}>{errors.email}</p>
                    )}
                  </div>

                  <div>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="Your phone number (optional)"
                      className={inputClasses()}
                    />
                  </div>

                  <div>
                    <textarea
                      name="message"
                      rows={4}
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Type your message here..."
                      className={inputClasses(errors.message)}
                    />
                    {errors.message && (
                      <p className={`mt-2 text-sm ${isDark ? 'text-red-400' : 'text-red-600'}`}>{errors.message}</p>
                    )}
                  </div>

                  <div className="flex justify-end space-x-4">
                    <motion.button
                      type="button"
                      onClick={() => setShowForm(false)}
                      className={`px-4 py-2 ${
                        isDark ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-800'
                      } transition-colors duration-200`}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      Cancel
                    </motion.button>
                    <motion.button
                      type="submit"
                      disabled={loading || checking || (existingContact && (existingContact.status === 'new' || existingContact.status === 'in-progress'))}
                      className={`inline-flex items-center px-6 py-3 rounded-md text-base font-semibold text-white shadow-sm transition-all duration-300 transform hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed ${
                        isDark 
                          ? 'bg-blue-600 hover:bg-blue-700'
                          : 'bg-blue-500 hover:bg-blue-600'
                      }`}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      {loading || checking ? (
                        <>
                          <FaSpinner className="animate-spin mr-3 h-5 w-5" />
                          <span>{loading ? 'Sending...' : 'Checking...'}</span>
                        </>
                      ) : (
                        <>
                          <FaPaperPlane className="mr-3 h-5 w-5" />
                          <span>Send Message</span>
                        </>
                      )}
                    </motion.button>
                  </div>
                </form>
              </motion.div>
            )}
          </motion.div>

          <motion.dl 
            className="grid grid-cols-1 gap-x-8 gap-y-10 sm:grid-cols-2 lg:pt-2"
            variants={containerVariants}
          >
            <motion.div 
              className="flex flex-col items-start"
              variants={itemVariants}
            >
              <div className={`rounded-md p-2 ${
                isDark ? 'bg-blue-900/30' : 'bg-blue-100'
              }`}>
                <FaEnvelope className={`h-6 w-6 ${
                  isDark ? 'text-blue-400' : 'text-blue-600'
                }`} />
              </div>
              <dt className={`mt-4 font-semibold ${
                isDark ? 'text-white' : 'text-gray-900'
              }`}>
                Email Us
              </dt>
              <dd className={`mt-2 leading-7 ${
                isDark ? 'text-gray-400' : 'text-gray-600'
              }`}>
                <a href="mailto:support@codeitronics.com" className="hover:underline">
                  support@codeitronics.com
                </a>
              </dd>
            </motion.div>

            <motion.div 
              className="flex flex-col items-start"
              variants={itemVariants}
            >
              <div className={`rounded-md p-2 ${
                isDark ? 'bg-blue-900/30' : 'bg-blue-100'
              }`}>
                <FaPhone className={`h-6 w-6 ${
                  isDark ? 'text-blue-400' : 'text-blue-600'
                }`} />
              </div>
              <dt className={`mt-4 font-semibold ${
                isDark ? 'text-white' : 'text-gray-900'
              }`}>
                Call Us
              </dt>
              <dd className={`mt-2 leading-7 ${
                isDark ? 'text-gray-400' : 'text-gray-600'
              }`}>
                <a href="tel:+1234567890" className="hover:underline">
                  +1 (234) 567-890
                </a>
              </dd>
            </motion.div>

            <motion.div 
              className="flex flex-col items-start"
              variants={itemVariants}
            >
              <div className={`rounded-md p-2 ${
                isDark ? 'bg-blue-900/30' : 'bg-blue-100'
              }`}>
                <FaGlobe className={`h-6 w-6 ${
                  isDark ? 'text-blue-400' : 'text-blue-600'
                }`} />
              </div>
              <dt className={`mt-4 font-semibold ${
                isDark ? 'text-white' : 'text-gray-900'
              }`}>
                Visit Us
              </dt>
              <dd className={`mt-2 leading-7 ${
                isDark ? 'text-gray-400' : 'text-gray-600'
              }`}>
                <a href="https://www.codeitronics.com" target="_blank" rel="noopener noreferrer" className="hover:underline">
                  www.codeitronics.com
                </a>
              </dd>
            </motion.div>
          </motion.dl>
        </motion.div>
      </motion.div>

      <div className="absolute left-1/2 top-0 -z-10 -translate-x-1/2 blur-3xl xl:-top-6" aria-hidden="true">
        <div
          className="aspect-[1155/678] w-[72.1875rem] bg-gradient-to-tr from-[#ff80b5] to-[#9089fc] opacity-30"
          style={{
            clipPath:
              'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)',
          }}
        />
      </div>
    </section>
  );
};

export default ContactCTA; 